import { Injectable } from '@angular/core';

import { LinkTypeEnum } from '../../Forms/enums/link-type.enum';
import { BlockModel } from '../../PageBuilder/models/block.model';
import { ButtonDisplayElementModel } from '../../PageBuilder/models/button-display-element.model';
import { ImageDisplayElementModel } from '../../PageBuilder/models/image-display-element.model';
import { RichTextDisplayElementModel } from '../../PageBuilder/models/rich-text-display-element.model';
import { LocalizationService } from '../../../Core/Localization/services/localization.service';


@Injectable()
export class PlainTextService {
    titleBlockSeparator = '-';
    textBlockHeadingPrefix = '*';
    indentSpace = ' '.repeat(4);
    footerSeparator: string = '=';

    constructor(
        protected localizationService: LocalizationService,
    ) {}

    generatePlainTextFromTitleBlock(block: BlockModel): string {
        const text = (block.displayElement as RichTextDisplayElementModel).text;
        if (text) {
            return text + '\n' + this.titleBlockSeparator.repeat(text.length + 10) + '\n';
        } else {
            return '\n' + this.titleBlockSeparator.repeat(10) + '\n';
        }
    }

    generatePlainTextFromTextBlock(block: BlockModel): string {
        return this.parseHTML((block.displayElement as RichTextDisplayElementModel).text);
    }

    parseHTML(str: string): string {
        if (!str) {
            return null;
        }

        const div = document.createElement('div');
        div.innerHTML = str.trim().replace(/(\r?\n|\r)\s*</g, '<');

        return this.parseNode(div);
    }

    parseHTMLWithoutMarkdown(str: string): string {
        if (!str) {
            return null;
        }

        const div = document.createElement('div');
        div.innerHTML = str.trim();

        return this.parseNodeWithoutMarkdown(div);
    }


    parseNodeWithoutMarkdown(node: HTMLElement): string {
        let plainText = '';
        if (node.nodeType === Node.TEXT_NODE) {
            if (node.nodeValue.trim() === '\n') {
                return plainText;
            }
            return plainText + node.textContent;
        }

        for (let i = 0; i < node.childNodes.length; i++) {
            plainText += this.parseNodeWithoutMarkdown(node.childNodes[i] as HTMLElement);
        }

        if (node.nodeType !== Node.TEXT_NODE) {
            const tagName = node.tagName;
            if (!tagName) {
                return plainText;
            }
            if ((tagName.startsWith('H') && tagName.length === 2)) {
                plainText += '\n';
            } else {
                switch (tagName) {
                    case 'P':
                        plainText += '\n';
                        break;
                    case 'LI':
                        if ((node.parentNode as HTMLElement).tagName === 'UL') {
                            plainText = plainText + '\n';
                        } else {
                            plainText = ((Array.from(node.parentNode.childNodes) as HTMLElement[])
                                .findIndex(childNode => childNode.innerText === node.innerText) + 1) + '. ' + plainText + '\n';
                        }
                        let indentLevel = 0;
                        const indentClass = node.className.indexOf('ql-indent-');
                        if (indentClass > -1) {
                            indentLevel = Number(node.className.substr(indentClass + 10, 1));
                        }
                        plainText = this.indentSpace.repeat(indentLevel + 1) + plainText;
                        break;
                }
            }
        }

        return plainText;
    }

    parseNode(node): string {
        let plainText = '';
        if (node.nodeType === Node.TEXT_NODE) {
            if (node.nodeValue.trim() === '\n') {
                return plainText;
            }
            return plainText + node.textContent;
        }

        for (let i = 0; i < node.childNodes.length; i++) {
            plainText += this.parseNode(node.childNodes[i]);
        }

        if (node.nodeType !== Node.TEXT_NODE) {
            const tagName = node.tagName;
            if (!tagName) {
                return plainText;
            }
            if ((tagName.startsWith('H') && tagName.length === 2)) {
                plainText = this.textBlockHeadingPrefix.repeat(Number(tagName[1])) + ' ' + plainText + '\n';
            } else {
                switch (tagName) {
                    case 'P':
                        plainText += '\n';
                        break;
                    case 'STRONG':
                        plainText = ' * ' + plainText + ' * ';
                        break;
                    case 'LI':
                        if (node.parentNode.tagName === 'UL') {
                            plainText = '- ' + plainText + '\n';
                        } else {
                            plainText = (Array.from(node.parentNode.childNodes as HTMLElement[])
                                .findIndex(childNode => childNode.innerText === node.innerText) + 1) + '. ' + plainText + '\n';
                        }
                        let indentLevel = 0;
                        const indentClass = node.className.indexOf('ql-indent-');
                        if (indentClass > -1) {
                            indentLevel = Number(node.className.substr(indentClass + 10, 1));
                        }
                        plainText = this.indentSpace.repeat(indentLevel + 1) + plainText;
                        break;
                    case 'A':
                        plainText = ` ## ${plainText} ## (${node.href})`;
                        break;
                    case 'SUP':
                        plainText = '^' + plainText;
                        break;
                    case 'SUB':
                        plainText = '_' + plainText;
                        break;
                    case 'BLOCKQUOTE':
                        plainText = this.indentSpace + '[ ' + plainText + ' ]\n';
                        break;
                }
            }
        }

        return plainText;
    }

    generatePlainTextFromImageBlock(block: BlockModel): string {
        const displayElement = block.displayElement as ImageDisplayElementModel;
        const alt = displayElement?.image?.altText ? `${displayElement.image.altText} ` : '';
        const caption = displayElement?.settings?.caption ? `"${displayElement.settings.caption}"` : '';
        const imageText = `${alt}${caption}`;
        const link = displayElement?.settings?.link;

        if (link) {
            if (link.type === LinkTypeEnum.URL) {
                return `${imageText} (${link.url})\n`;
            } else if (link.type === LinkTypeEnum.EMAIL) {
                return `${imageText} (mailto: ${link.email})\n`;
            }
        } else {
            return imageText ? `${imageText}\n` : this.localizationService.transform('Common.image');
        }
    }

    generatePlainTextFromButtonBlock(block: BlockModel): string {
        const displayElement = block.displayElement as ButtonDisplayElementModel;
        const text = displayElement.text;
        const url = displayElement.url;


        if (url.type === LinkTypeEnum.EMAIL) {
            return `<<< ${text} >>> (mailto: ${url.email})\n`;
        } else {
            return `<<< ${text} >>> (${url.url})\n`;
        }
    }
}
